import { useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useGames } from '@/features/games/games-queries';
import { PLATFORMS, Platform, VersionMapping, CreateVersionMappingInput, UpdateVersionMappingInput } from './version-mappings-types';
import { VersionMappingForm } from './version-mappings-form';
import {
  useCreateVersionMapping,
  useDeleteVersionMapping,
  useUpdateVersionMapping,
  useVersionMappings,
} from './version-mappings-queries';
import { formatDate } from '@/lib/format';

export function VersionMappingsPage(): JSX.Element {
  const { data: games } = useGames();
  const [filterGameId, setFilterGameId] = useState<string>('');
  const [filterPlatform, setFilterPlatform] = useState<Platform | ''>('');
  const { data: mappings, isLoading, error } = useVersionMappings({
    game_id: filterGameId || undefined,
    platform: filterPlatform || undefined,
  });
  const createMut = useCreateVersionMapping();
  const updateMut = useUpdateVersionMapping();
  const deleteMut = useDeleteVersionMapping();

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<VersionMapping | null>(null);
  const [deleting, setDeleting] = useState<VersionMapping | null>(null);

  function gameName(id: string): string {
    return games?.find((g) => g.id === id)?.package_name ?? id.slice(0, 8);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Version Mappings</h2>
          <p className="text-sm text-muted-foreground">
            Map semver ranges to baseUrl per (game, platform).
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={filterGameId || 'all'} onValueChange={(v) => setFilterGameId(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-56">
              <SelectValue placeholder="All games" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All games</SelectItem>
              {games?.map((g) => (
                <SelectItem key={g.id} value={g.id}>{g.package_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterPlatform || 'all'} onValueChange={(v) => setFilterPlatform(v === 'all' ? '' : (v as Platform))}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="All platforms" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All platforms</SelectItem>
              {PLATFORMS.map((p) => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> New Mapping
          </Button>
        </div>
      </div>

      {isLoading && <p className="text-muted-foreground">Loading…</p>}
      {error && <p className="text-destructive">Failed to load mappings.</p>}

      {mappings && mappings.length === 0 && (
        <div className="rounded-lg border border-dashed p-8 text-center">
          <p className="text-muted-foreground">No version mappings yet.</p>
        </div>
      )}

      {mappings && mappings.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Game</TableHead>
              <TableHead>Platform</TableHead>
              <TableHead>Range</TableHead>
              <TableHead>Base URL</TableHead>
              <TableHead>Active</TableHead>
              <TableHead>Updated</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {mappings.map((m) => (
              <TableRow key={m.id}>
                <TableCell className="font-mono text-sm">{gameName(m.game_id)}</TableCell>
                {/* Red Team F#15: shadcn Badge does not exist in components/ui/; use plain text. */}
                <TableCell className="font-mono text-sm">{m.platform}</TableCell>
                <TableCell className="font-mono text-sm">
                  {m.min_version} → {m.max_version ?? '∞'}
                </TableCell>
                <TableCell className="font-mono text-sm">{m.base_url}</TableCell>
                <TableCell>{m.is_active ? '✓' : '—'}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{formatDate(m.updated_at)}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(m)} aria-label="Edit">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setDeleting(m)} aria-label="Delete">
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Version Mapping</DialogTitle>
          </DialogHeader>
          {games && (
            <VersionMappingForm
              games={games}
              onSubmit={async (data) => {
                await createMut.mutateAsync(data as CreateVersionMappingInput);
                setCreateOpen(false);
              }}
              onCancel={() => setCreateOpen(false)}
              isSubmitting={createMut.isPending}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Version Mapping</DialogTitle>
          </DialogHeader>
          {editing && games && (
            <VersionMappingForm
              initial={editing}
              games={games}
              onSubmit={async (data) => {
                await updateMut.mutateAsync({ id: editing.id, input: data as UpdateVersionMappingInput });
                setEditing(null);
              }}
              onCancel={() => setEditing(null)}
              isSubmitting={updateMut.isPending}
            />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete version mapping?</AlertDialogTitle>
            <AlertDialogDescription>
              This will affect mobile clients on <code>{deleting?.platform} {deleting?.min_version}</code> immediately
              (cache TTL 60s).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (deleting) {
                  await deleteMut.mutateAsync(deleting.id);
                  setDeleting(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
